import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import { getLocalDateString } from '../utils/date';
import { API_BASE_URL } from '../services/api';

export interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  type?: 'app' | 'nutrition' | 'general';
  photoUrl?: string;
  mealSuggestions?: any[];
}

export interface PendingMeal {
  foods: any[];       // Array of individual food components
  meal: any;          // Backward compat: foods[0]
  mealType: string;
  date: string;
}

export interface PendingExercise {
  exercise: any;
  date: string;
}

export interface UpgradeNudgeState {
  usageType?: 'food' | 'analysis' | 'assistant' | 'camera' | 'voice';
  message?: string;
  title?: string;
}

interface AIBarContextType {
  isExpanded: boolean;
  isLoading: boolean;
  messages: Message[];
  pendingMeal: PendingMeal | null;
  pendingExercise: PendingExercise | null;
  lastPhotoUrl: string | null;
  lastPhotoPublicId: string | null;
  lastPhotoFileRef: React.MutableRefObject<File | null>;
  sessionId: string | null;
  correctionCount: number;
  expand: () => void;
  minimize: () => void;
  toggle: () => void;
  addMessage: (message: Message) => void;
  setMessages: (messages: Message[]) => void;
  setLoading: (loading: boolean) => void;
  clearMessages: () => void;
  setPendingMeal: (meal: PendingMeal | null) => void;
  setPendingExercise: (exercise: PendingExercise | null) => void;
  setLastPhotoUrl: (url: string | null) => void;
  setLastPhotoPublicId: (id: string | null) => void;
  setSessionId: (id: string | null) => void;
  setCorrectionCount: (count: number) => void;
  upgradeNudge: UpgradeNudgeState | null;
  showUpgradeNudge: (opts: UpgradeNudgeState) => void;
  hideUpgradeNudge: () => void;
}

const AIBarContext = createContext<AIBarContextType | undefined>(undefined);

export const useAIBar = () => {
  const context = useContext(AIBarContext);
  if (!context) {
    throw new Error('useAIBar must be used within AIBarProvider');
  }
  return context;
};

interface AIBarProviderProps {
  children: ReactNode;
}

export const AIBarProvider: React.FC<AIBarProviderProps> = ({ children }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingMeal, setPendingMealState] = useState<PendingMeal | null>(null);
  const [pendingExercise, setPendingExerciseState] = useState<PendingExercise | null>(null);
  const [lastPhotoUrl, setLastPhotoUrlState] = useState<string | null>(null);
  const [lastPhotoPublicId, setLastPhotoPublicIdState] = useState<string | null>(null);
  const [sessionId, setSessionIdState] = useState<string | null>(null);
  const [correctionCount, setCorrectionCountState] = useState(0);
  const lastPhotoFileRef = useRef<File | null>(null);
  const foodLoggedDuringSession = React.useRef(false);
  // Central upgrade-nudge modal — rendered by PersistentAIBar (always mounted),
  // triggerable from any component/hook at the exact moment a limit denies access
  const [upgradeNudge, setUpgradeNudge] = useState<UpgradeNudgeState | null>(null);
  const showUpgradeNudge = useCallback((opts: UpgradeNudgeState) => setUpgradeNudge(opts), []);
  const hideUpgradeNudge = useCallback(() => setUpgradeNudge(null), []);

  // Listen for foodLogged events to track if food was logged while chat is open
  React.useEffect(() => {
    const handleFoodLogged = () => {
      if (isExpanded) {
        foodLoggedDuringSession.current = true;
      }
    };
    window.addEventListener('foodLogged', handleFoodLogged);
    window.addEventListener('photoFoodLogged', handleFoodLogged);
    return () => {
      window.removeEventListener('foodLogged', handleFoodLogged);
      window.removeEventListener('photoFoodLogged', handleFoodLogged);
    };
  }, [isExpanded]);

  const expand = useCallback(() => {
    foodLoggedDuringSession.current = false;
    setIsExpanded(true);
  }, []);

  const minimize = useCallback(() => {
    setIsExpanded(false);
    // If food was logged during this chat session, dispatch a refresh event
    // so any page (Dashboard, FoodLog, etc.) picks up the changes
    if (foodLoggedDuringSession.current) {
      foodLoggedDuringSession.current = false;
      window.dispatchEvent(new CustomEvent('foodLogged', { detail: { date: getLocalDateString(new Date()) } }));
    }
  }, []);

  const toggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Seed a welcome note for users who haven't used the app yet (it no longer opens the chat by itself)
  // Checks server-side (food log count) — most reliable new user signal
  React.useEffect(() => {
    let cancelled = false;

    const checkAndShowWelcome = async () => {
      // Use sessionStorage (clears when tab closes) not localStorage (persists forever)
      if (sessionStorage.getItem('aiWelcomeChecked') || isExpanded || cancelled) return;
      sessionStorage.setItem('aiWelcomeChecked', 'true');

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const res = await fetch(`${API_BASE_URL}/food/logs?page=1&limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const hasData = data.data && data.data.length > 0;

        // Skip AI welcome if user came through the new onboarding funnel
        const completedOnboarding = localStorage.getItem('onboarding_demo_used') === 'true'
          || sessionStorage.getItem('onboarding_completed') === 'true';

        // The welcome waits in the chat instead of opening over the screen (2026-11-03): the
        // Dashboard's day-one cards are the first thing a new person should see.
        if (!hasData && !cancelled && !completedOnboarding) {
          setMessages((prev) => (prev.length ? prev : [{
            role: 'assistant',
            content: "Welcome to FuelTron.\n\nTell me what you last ate and I'll log it, every nutrient included. Ask about your numbers any time, or log a workout in plain words, like *\"I ran 3 miles.\"*",
            timestamp: new Date(),
            type: 'general',
          }]));
        }
      } catch {
        // Silently fail
      }
    };

    // Listen for disclosure accepted event
    window.addEventListener('disclosureAccepted', () => {
      sessionStorage.removeItem('aiWelcomeChecked'); // Reset so check runs again
      setTimeout(checkAndShowWelcome, 800);
    });

    // Also check after a delay for users who already accepted disclosure
    const timer = setTimeout(checkAndShowWelcome, 2000);

    return () => { cancelled = true; clearTimeout(timer); };
  }, []);

  const addMessage = useCallback((message: Message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const setPendingMeal = useCallback((meal: PendingMeal | null) => {
    setPendingMealState(meal);
  }, []);

  const setPendingExercise = useCallback((exercise: PendingExercise | null) => {
    setPendingExerciseState(exercise);
  }, []);

  const setLastPhotoUrl = useCallback((url: string | null) => {
    setLastPhotoUrlState(url);
  }, []);

  const setLastPhotoPublicId = useCallback((id: string | null) => {
    setLastPhotoPublicIdState(id);
  }, []);

  const setSessionId = useCallback((id: string | null) => {
    setSessionIdState(id);
  }, []);

  const setCorrectionCount = useCallback((count: number) => {
    setCorrectionCountState(count);
  }, []);

  const value: AIBarContextType = {
    isExpanded,
    isLoading,
    messages,
    pendingMeal,
    pendingExercise,
    lastPhotoUrl,
    lastPhotoPublicId,
    lastPhotoFileRef,
    sessionId,
    correctionCount,
    expand,
    minimize,
    toggle,
    addMessage,
    setMessages,
    setLoading,
    clearMessages,
    setPendingMeal,
    setPendingExercise,
    setLastPhotoUrl,
    setLastPhotoPublicId,
    setSessionId,
    setCorrectionCount,
    upgradeNudge,
    showUpgradeNudge,
    hideUpgradeNudge,
  };

  return <AIBarContext.Provider value={value}>{children}</AIBarContext.Provider>;
};
